/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

import ErrorName from '../../../core/error/error-name';

/**
 * Správce firmware pro standardní webové technologie.
 * @implements {FirmwareManager}
 * @memberof AdapterWeb
 */
class FirmwareManager {
  /**
   * Vytvoří instanci.
   * @param {Object} uaParser Knihovna UAParser.js.
   * @returns {FirmwareManager} Nová instance.
   */
  constructor(uaParser) {
    this.uaParser = uaParser;
  }

  /**
   * Zjistí část názvu modelu s detekovaným prohlížečem.
   * @private
   * @returns {string} Část názvu modelu.
   */
  getBrowser() {
    const { major, name } = this.uaParser.getBrowser();
    if (isNil(name)) {
      return '';
    }
    return `${name} ${major}`;
  }

  /**
   * Zjistí část názvu modelu s detekovaným vykreslovacím jádrem.
   * @private
   * @returns {string} Část názvu modelu.
   */
  getEngine() {
    const { name, version } = this.uaParser.getEngine();
    if (isNil(name)) {
      return '';
    }
    return `${name} ${version}`;
  }

  /** @inheritdoc */
  getInfo() {
    // Web neumožňuje zjistit informace o firmware.
    return new Promise((resolve) => resolve({}));
  }

  /** @inheritdoc */
  getManufacturer() {
    return new Promise((resolve) => {
      const manufacturer = this.uaParser.getDevice().vendor;
      if (isNil(manufacturer)) {
        const error = new Error('Unable to get the manufacturer.');
        error.name = ErrorName.NotSupportedError;
        throw error;
      }
      return resolve(manufacturer);
    });
  }

  /**
   * Zjistí část názvu modelu s detekovaným názvem modelu.
   * @private
   * @returns {string} Část názvu modelu.
   */
  getModelExact() {
    const { model } = this.uaParser.getDevice();
    if (isNil(model)) {
      return '';
    }
    return model;
  }

  /** @inheritdoc */
  getModelName() {
    return new Promise((resolve) => {
      const stringArray = [
        this.getModelExact(),
        this.getBrowser(),
        this.getEngine(),
        this.getOs()
      ];
      const modelName = stringArray.filter((string) => string).join(', ');
      if (isNil(modelName)) {
        const error = new Error('Unable to get the model name.');
        error.name = ErrorName.NotSupportedError;
        throw error;
      }
      return resolve(modelName);
    });
  }

  /**
   * Zjistí část názvu modelu s detekovaným operačním systémem.
   * @private
   * @returns {string} Část názvu modelu.
   */
  getOs() {
    const { name, version } = this.uaParser.getOS();
    if (isNil(name)) {
      return '';
    }
    return `${name} ${version}`;
  }

  /** @inheritdoc */
  getSerialNumber() {
    return new Promise(() => {
      const error = new Error('Unable to get the serial number.');
      error.name = ErrorName.NotSupportedError;
      throw error;
    });
  }

  /** @inheritdoc */
  getVersion() {
    return new Promise(() => {
      const error = new Error('Unable to get the firmware version.');
      error.name = ErrorName.NotSupportedError;
      throw error;
    });
  }

  /** @inheritdoc */
  update(firmwareRelease) {
    return new Promise(() => {
      const error = new Error('Unable to do any update.');
      error.name = ErrorName.NotSupportedError;
      throw error;
    });
  }
}

export default FirmwareManager;
